/**
 * Motivo sintetico della chiusura di un Ticket (badge della tabella "Ticket chiusi").
 *
 * Nessun campo dedicato lato server: il motivo si ricava dai dati già presenti.
 *  - promosso a report ENISA (status `promoted` o `srpReportId` valorizzato) → "Promosso a report
 *    ENISA" (con gli id dei report, per i link al dettaglio: uno per release del ticket);
 *  - chiuso dal lead/collaboratore (status `closed`): la chiusura in UI marca il prodotto come
 *    "non impattato"; se però tutte le CVE del ticket hanno lo stesso stato VEX "terminale"
 *    `FALSE_POSITIVE` o `RESOLVED`, il badge lo dice in modo più preciso.
 * La motivazione libera (`closedReason`) resta nel dettaglio e nel tooltip del badge.
 */

import type { Ticket } from './types.js';

export type TipoMotivoChiusura = 'promosso' | 'non_impattato' | 'falso_positivo' | 'risolto';

export interface MotivoChiusura {
  tipo: TipoMotivoChiusura;
  etichetta: string;
  /** Report ENISA (bozza SRP) generato dalla promozione: solo per `promosso`. */
  reportId?: number;
  /** Tutti i report generati dalla promozione (ticket su più release → più report). */
  reportIds?: number[];
}

const ETICHETTE: Record<TipoMotivoChiusura, string> = {
  promosso: 'Promosso a report ENISA',
  non_impattato: 'Prodotto non impattato',
  falso_positivo: 'Falso positivo',
  risolto: 'Vulnerabilità risolta',
};

/** Report ENISA del ticket promosso: la lista completa, o il solo primo per i promossi storici. */
export function reportDelTicket(ticket: Pick<Ticket, 'srpReportId' | 'srpReportIds'>): number[] {
  const tutti = [
    ...(ticket.srpReportId !== undefined ? [ticket.srpReportId] : []),
    ...(ticket.srpReportIds ?? []),
  ];
  return [...new Set(tutti)];
}

export function motivoChiusura(
  ticket: Pick<Ticket, 'status' | 'srpReportId' | 'srpReportIds' | 'payload'>,
): MotivoChiusura {
  if (ticket.status === 'promoted' || ticket.srpReportId !== undefined) {
    const reportIds = reportDelTicket(ticket);
    return {
      tipo: 'promosso',
      etichetta: ETICHETTE.promosso,
      ...(reportIds.length > 0 ? { reportId: reportIds[0], reportIds } : {}),
    };
  }
  const stati = ticket.payload.cves.map((c) => (c.analysisState ?? '').toUpperCase());
  const tutte = (s: string) => stati.length > 0 && stati.every((x) => x === s);
  const tipo: TipoMotivoChiusura = tutte('FALSE_POSITIVE')
    ? 'falso_positivo'
    : tutte('RESOLVED')
      ? 'risolto'
      : 'non_impattato';
  return { tipo, etichetta: ETICHETTE[tipo] };
}

/** Un ticket chiuso si può riaprire solo se non è mai stato promosso a report ENISA. */
export function isRiapribile(ticket: Pick<Ticket, 'status' | 'srpReportId'>): boolean {
  return ticket.status === 'closed' && ticket.srpReportId === undefined;
}
